<template>
  <div class="sp_agent_chat_widget_v2" v-if="isVisible" v-show="loaded" :style="chatThemeColorStyle">
    <ChatInterface
      :layout="isLayout"
      :messages="chatMessages"
      :suggestions="chatSuggestions"
      :is-loading="isLoading"
      :is-chat-running="isChatRunning"
      :is-searching="isSearching"
      :is-navigation-pending="isNavigationPending"
      :pending-navigation-url="pendingNavigation ? pendingNavigation.url : null"
      :is-fullscreen="isFullscreenMode"
      :title="config.title || 'Chat Assistant'"
      :subtitle="getSubtitle"
      :placeholder="config.placeholder"
      :avatar-image="avatarImage"
      :video-greeter="videoGreeter"
      :has-playlist-items="playlist && playlist.length > 0"
      :playlist="playlist"
      :is-playlist-loading="isPlaylistLoading"
      :current-content="currentContent"
      :is-content-panel-open="isContentPanelOpen"
      :session-id="sessionId"
      @expand="handleExpand"
      @set-layout="handleSetLayout"
      @toggle-align="handleToggleAlign"
      @fullscreen="handleFullscreen"
      @send="handleSendMessage"
      @onSuggestionClick="handleSuggestionClick"
      @open-content="handleOpenContent"
      @onChatStop="handleChatStop"
      @stop-audio="stopSpeechAudio"
      @play-welcome-greeting="handlePlayWelcomeGreeting"
      @navigateToUrl="navigateToUrl"
      :has-prev="hasPrev"
      :has-next="hasNext"
      @select-playlist-content="handleSelectPlaylistContent"
      @playlist-prev="handlePlaylistPrev"
      @playlist-next="handlePlaylistNext"
      @close-content="handleCloseContent"
      @content-progress="handleContentProgress"
      @showMeetingBooking="showMeetingBooking"
      ref="chatInterface"
    />

    <AgentCursor v-show="isElementGuideRunning" ref="cursor" />

    <MeetingWidgetModal
      v-if="isMeetingOpen && meetingConfiguration"
      :title="meetingConfiguration.title"
      :url="meetingConfiguration.booking_url"
      @close="isMeetingOpen = false"
      @onSuccess="handleMeetingSuccess"
    />
  </div>
</template>

<script>
import { timeLogger } from "../utils/timer";
import ChatInterface from "./components/ChatInterface.vue";
import AgentCursor from "./cursor/index.vue";
import { getElementTopLeftScreenCordinate } from "./cursor/utils";
import AudioSpeechMixin from "./mixins/AudioSpeech.js";
import MediaQueryMixin from "./mixins/mediaQuery.js";
import MeetingWidgetModal from "./components/components/MeetingWidget/modal.vue";
import Sound from "../utils/sound/index.js";
import { parse } from "partial-json";
import { LAYOUTS } from "./constants/layouts.js";
import GREETINGS from "./constants/voice.js";

function generateWelcomeMessage() {
  const welcomeMessages = [
    "Hello! I'm here to help you. How can I assist you today?",
    "Hi there! I'm here to assist you. What do you need help with?",
    "Greetings! How can I assist you today?",
    "Hey! I'm here to help. Feel free to ask me anything.",
    "Welcome! I'm here to assist you. How can I be of service?",
    "Hello! I'm at your service. How can I help?",
    "Hi! Let me know what you need.",
    "Greetings! How can I assist you today?",
    "Hey there! I'm here to help. Ask me anything you need.",
    "Welcome! How may I assist you today?",
    "Hello! How can I assist you today?",
    "Hi there! How can I help you today?",
    "Greetings! How can I be of assistance?",
    "Hey! How can I help you today?",
    "Welcome! What can I do for you today?",
  ];

  const randomIndex = Math.floor(Math.random() * welcomeMessages.length);
  return welcomeMessages[randomIndex];
}

function generateMeetingSuccessMessage() {
  const meetingSuccessMessages = [
    "Meeting booked successfully! We look forward to connecting with you.",
    "Your meeting has been scheduled. You'll receive a confirmation shortly.",
    "All set! Your meeting is confirmed and on the calendar.",
    "Meeting confirmed! We're excited to connect with you soon.",
    "You're booked! Expect a confirmation email with all the details.",
    "Great choice! Your meeting is now scheduled.",
    "Done! Your meeting slot is reserved. See you soon!",
    "Meeting booked! We'll see you at the scheduled time.",
  ];

  const randomIndex = Math.floor(Math.random() * meetingSuccessMessages.length);

  return meetingSuccessMessages[randomIndex];
}

function cancellableSleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal.aborted) {
      return resolve();
    }

    const timeoutId = setTimeout(() => {
      resolve();
    }, ms);

    signal.addEventListener("abort", () => {
      clearTimeout(timeoutId);
      resolve();
    });
  });
}

function parseStreamChunk(input, callback) {
  const regex = /data:\s*(\{.*?\})(?=\s*(data:|$))/gs;

  let match;
  while ((match = regex.exec(input)) !== null) {
    try {
      callback(parse(match[1]));
    } catch (error) {
      callback(null);
    }
  }
}

const DEFAULT_PRIMARY_COLOR = "#0b3398";
const DEFAULT_PRIMARY_COLOR_RGB = "11, 51, 152";

export default {
  name: "AgentChatWidgetV2",
  mixins: [AudioSpeechMixin, MediaQueryMixin],
  components: {
    ChatInterface,
    AgentCursor,
    MeetingWidgetModal,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
    autoOpen: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: String,
      default: LAYOUTS.INPUT,
    },
  },
  data() {
    const getInitialLayout = () => {
      if (this.layout === LAYOUTS.ICON) return LAYOUTS.ICON;
      if (this.layout === LAYOUTS.INPUT) return LAYOUTS.INPUT;
      if (this.layout === LAYOUTS.SIDEBAR) return LAYOUTS.SIDEBAR;
      if (this.layout === LAYOUTS.FULL) return LAYOUTS.FULL;
      if (this.layout === LAYOUTS.EXPANDED) return LAYOUTS.EXPANDED;
      return LAYOUTS.INPUT;
    };

    return {
      loaded: false,

      isVisible: true,
      isLayout: getInitialLayout(),
      isContentPanelOpen: false,
      isLoading: false,
      isChatRunning: false,
      isSearching: false,

      initialMessages: [],
      chatMessages: [],
      chatSuggestions: [],
      audioUrl: null,

      pendingNavigation: null,
      isNavigationPending: false,

      currentContent: null,
      config: {},
      sessionId: null,
      demoCenter: null,
      initialChatOpen: false,

      isPlaylistLoading: true,
      playlist: [],
      resourceCollection: [],
      resources: [],

      triggerElementGuideCleanUp: null,
      elementGuideAbortController: null,
      isElementGuideRunning: false,
      messageAbortController: null,
      pageElements: [],

      chatAction: null,

      isMeetingOpen: false,
      pendingMeetingBooking: false,
    };
  },
  created() {
    this.loadSnapshot();

    if (this.chatAction === "resume") {
      this.isLayout = LAYOUTS.SIDEBAR;
    }

    if (this.layout === LAYOUTS.FULL) {
      this.isContentPanelOpen = true;
    }
  },
  mounted() {
    this.init();
    if (!this.isDesktop && (this.isLayout === LAYOUTS.SIDEBAR || this.isLayout === LAYOUTS.EXPANDED)) {
      this.isLayout = LAYOUTS.FULL;
      this.isContentPanelOpen = true;
    }
  },
  beforeDestroy() {
    document.body.classList.remove("sp_agent_chat_sidebar_space");
    document.body.classList.remove("sp_agent_chat_sidebar_space_expanded");

    if (this.triggerElementGuideCleanUp) {
      this.triggerElementGuideCleanUp();
    }
  },
  computed: {
    isExpanded() {
      return this.isLayout === LAYOUTS.EXPANDED || this.isLayout === LAYOUTS.SIDEBAR || this.isLayout === LAYOUTS.FULL;
    },
    isIcon() {
      return this.isLayout === LAYOUTS.ICON;
    },
    isSidebarAligned() {
      return this.isLayout === LAYOUTS.SIDEBAR;
    },
    isFullscreenMode() {
      return this.isLayout === LAYOUTS.FULL;
    },
    hasPrev() {
      if (!this.currentContent || !this.playlist?.length) return false;
      const idx = this.playlist.findIndex(
        (item) =>
          item.content_id === this.currentContent.content_id &&
          item.content_type === this.currentContent.content_type
      );
      return idx > 0;
    },
    hasNext() {
      if (!this.currentContent || !this.playlist?.length) return false;
      const idx = this.playlist.findIndex(
        (item) =>
          item.content_id === this.currentContent.content_id &&
          item.content_type === this.currentContent.content_type
      );
      return idx >= 0 && idx < this.playlist.length - 1;
    },
    getSubtitle() {
      if (this.isLoading) return "Typing...";
      if (this.config.subtitle) return this.config.subtitle;
      return "Online";
    },
    avatarImage() {
      return this.demoCenter?.settings?.avatar_image || `${process.env.VUE_APP_SHARE_URL}/placeholders/images/agent-avatar-icon.png`;
    },
    settingsMeetingEnabled() {
      return Boolean(![null, undefined].includes(this.demoCenter?.settings?.meeting_enabled) ? this.demoCenter.settings.meeting_enabled : false) && this.demoCenter?.settings?.meeting_configuration_id !== null;
    },
    meetingConfiguration() {
      return this.demoCenter?.settings?.meeting_configuration || null;
    },
    publicId() {
      return this.demoCenter?.publicId || this.token;
    },
    chatThemeColorStyle() {
      const color = this.demoCenter?.settings?.theme_primary_color || DEFAULT_PRIMARY_COLOR;
      
      if (!color) return {};

      try {
        const hex = color.replace("#", "");
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return {
          "--sp-chat-primary": color,
          "--sp-chat-primary-rgb": `${r}, ${g}, ${b}`,
        };
      } catch (error) {
        return {
          "--sp-chat-primary": DEFAULT_PRIMARY_COLOR,
          "--sp-chat-primary-rgb": DEFAULT_PRIMARY_COLOR_RGB,
        };
      }
    },
    videoGreeter() {
      return this.demoCenter?.settings?.video_greeter || 'agent-alex';
    },
  },
  methods: {
    async init() {
      try {
        await this.fetchConfiguration();

        this.loaded = true;

        Promise.all([this.loadPlaylist(), this.getDemoCenterResources()]).then(() => {
          this.resourceCollection = [...this.playlist, ...this.resources];
          if (this.isLayout === LAYOUTS.FULL && this.playlist?.length > 0 && !this.currentContent) {
            this.handleSelectPlaylistContent(this.playlist[0]);
          }
        });

        this.sessionId = this.generateSessionId();

        if (this.initialMessages.length > 0) {
          this.chatMessages = [...this.initialMessages];
        } else {
          this.showWelcomeMessage();
        }
      } catch (error) {
        console.error("[Agent Chat Widget V2] Initialization error:", error);
      }
    },
    getStorageKey() {
      return `sp_agent_chat_v2_snapshot_${this.publicId}`;
    },
    saveSnapshot(options = {}) {
      try {
        const storageKey = this.getStorageKey();
        const data = {
          messages: this.chatMessages,
          sessionId: this.sessionId,
          timestamp: Date.now(),
          suggestions: this.chatSuggestions,
          pendingNavigation: this.pendingNavigation,
          ...options,
        };
        sessionStorage.setItem(storageKey, JSON.stringify(data));
      } catch (error) {
        console.error("[Agent Chat Widget V2] Failed to save snapshot to storage:", error);
      }
    },
    loadSnapshot() {
      try {
        const storageKey = this.getStorageKey();
        const stored = sessionStorage.getItem(storageKey);
        if (!stored) return;

        const data = JSON.parse(stored);
        this.initialMessages = data.messages;
        this.sessionId = data.sessionId;
        this.chatAction = data.action;
        this.chatSuggestions = data.suggestions || [];
        this.pendingNavigation = data.pendingNavigation || null;

        this.$nextTick(() => {
          this.$refs.chatInterface.setChatStarted(true);
          this.$refs.chatInterface.scrollToBottom();
          this.processPendingNavigationMessage();
        });
      } catch (error) {
        console.error("[Agent Chat Widget V2] Failed to load snapshot from storage:", error);
      }
    },
    async fetchConfiguration() {
      try {
        let decodedToken = this.token;
        try {
          decodedToken = atob(this.token);
          const tokenData = JSON.parse(decodedToken);
          this.config = tokenData;
        } catch (e) {
          decodedToken = this.token;
        }

        const response = await this.axios.get(`/frontend/demo-center/view/${this.token}`, {
          params: { settings: true },
        });
        const { data } = response.data;

        if (data) {
          this.demoCenter = data;

          console.log(this.demoCenter);

          this.config = {
            ...this.config,
            title: this.demoCenter.name || "Chat Assistant",
            welcomeMessage: this.demoCenter.welcome_message || "Welcome to the chat!",
            placeholder: this.demoCenter.chat_placeholder,
          };
        }
      } catch (error) {
        console.error("[Agent Chat Widget V2] Config fetch error:", error);
      }
    },
    handleExpand() {
      if (!this.isDesktop && (this.isLayout === LAYOUTS.ICON || this.isLayout === LAYOUTS.INPUT)) {
        this.handleSetLayout(LAYOUTS.FULL);
        return;
      }
      if (this.isLayout === LAYOUTS.ICON) {
        this.animateLayout(LAYOUTS.SIDEBAR);
        this.handleSetLayout(LAYOUTS.SIDEBAR);
      } else if (this.isLayout === LAYOUTS.INPUT) {
        this.handleSetLayout(LAYOUTS.EXPANDED);
      } else if (this.isLayout === LAYOUTS.EXPANDED || this.isLayout === LAYOUTS.SIDEBAR || this.isLayout === LAYOUTS.FULL) {
        this.handleSetLayout(LAYOUTS.EXPANDED);
      }
    },
    handleSetLayout(layout) {
      if (layout === LAYOUTS.FULL) {
        this.isLayout = layout;
        if (this.playlist && this.playlist.length > 0 && !this.currentContent) {
          this.handleSelectPlaylistContent(this.playlist[0]);
        } else if (this.currentContent) {
          this.isContentPanelOpen = true;
        }
      } else {
        this.isLayout = layout;
        this.handleCloseContent();
      }
    },
    handleToggleAlign() {
      if (this.isLayout === LAYOUTS.EXPANDED) {
        this.handleSetLayout(LAYOUTS.SIDEBAR);
      } else if (this.isLayout === LAYOUTS.SIDEBAR) {
        this.handleSetLayout(LAYOUTS.EXPANDED);
      }
    },
    handleFullscreen() {
      if (this.isLayout === LAYOUTS.FULL) {
        this.handleSetLayout(LAYOUTS.SIDEBAR);
      } else {
        this.handleSetLayout(LAYOUTS.FULL);
      }
    },
    handleSidebarAlign() {
      if (this.isLayout === LAYOUTS.EXPANDED) {
        this.isLayout = LAYOUTS.SIDEBAR;
      }
    },
    animateLayout(layout) {
      if (layout === LAYOUTS.SIDEBAR) {
        this.$refs.chatInterface.getRootElement().animate(
          [
            { opacity: 0, bottom: "50%", transform: "translate(100%, 50%)" },
            { opacity: 1, bottom: "50%", transform: "translate(0%, 50%)" },
          ],
          {
            duration: 200,
            easing: "cubic-bezier(0.4, 0, 0.2, 1)",
          }
        );
      }
    },
    async handleSendMessage(text, source = "text") {
      if (!text || !text.trim()) return;

      if (this.messageAbortController) {
        this.messageAbortController.abort();
      }

      this.stopSpeechAudio();

      const timeLog = timeLogger();

      this.messageAbortController = new AbortController();
      const signal = this.messageAbortController.signal;

      this.chatSuggestions = [];

      this.addUserMessage(text);

      this.isChatRunning = true;

      const timerId = setTimeout(() => {
        if (!signal.aborted) {
          this.isLoading = true;
          this.$nextTick(() => {
            this.$refs.chatInterface.scrollToBottom();
          });
        }
      }, 800);

      Sound.tap();

      try {
        const messageIndex = this.addAiMessage("", [], "chat");

        let response = {
          navigateUrl: null,
        };

        await this.queryLlmModel({ question: text, speak: source === "voice" }, signal, (chunk) => {
          try {
            parseStreamChunk(chunk, (result) => {
              const payload = result.payload;
              const type = result.type;

              switch (type) {
                case "message":
                  if (this.isNavigationPending) {
                    if (!this.pendingNavigation) this.pendingNavigation = { timestamp: Date.now() };
                    this.pendingNavigation.message = payload;
                  } else {
                    response.message = payload;
                    this.updateAiMessage(messageIndex, { message: payload });

                    this.$nextTick(() => {
                      this.$refs.chatInterface.smoothScrollToLastMessage();

                      this.$nextTick(() => {
                        this.$refs.chatInterface.checkIfAtBottom();
                      });
                    });
                  }
                  break;
                case "speak":
                  if (this.isNavigationPending) {
                    if (!this.pendingNavigation) this.pendingNavigation = { timestamp: Date.now() };
                    this.pendingNavigation.audioUrl = payload;
                  } else {
                    this.audioUrl = payload;
                    this.playSpeechAudio(this.audioUrl);
                  }
                  break;
                case "suggestions":
                  try {
                    const suggestions = typeof payload === "string" ? parse(payload) : payload;
                    if (this.isNavigationPending) {
                      if (!this.pendingNavigation) this.pendingNavigation = { timestamp: Date.now() };
                      this.pendingNavigation.suggestions = suggestions;
                    } else {
                      this.chatSuggestions = suggestions;
                    }
                  } catch (error) {
                    console.log(error);
                    if (!this.isNavigationPending) {
                      this.chatSuggestions = [];
                    }
                  }
                  break;
                case "navigate-url":
                  this.isNavigationPending = true;
                  this.pendingNavigation = {
                    url: payload.url,
                    timestamp: Date.now(),
                    message: this.pendingNavigation?.message || null,
                    audioUrl: this.pendingNavigation?.audioUrl || null,
                  };
                  response.navigateUrl = payload.url;
                  break;
                case "recommendations":
                  try {
                    response.recommendations = typeof payload === "string" ? parse(payload) : payload;
                  } catch (error) {
                    response.recommendations = [];
                  }
                  break;
                case "elements":
                  try {
                    this.pageElements = typeof payload === "string" ? parse(payload) : payload;
                  } catch (error) {
                    this.pageElements = [];
                  }
                  break;
                case "tool-search-start":
                  this.isSearching = true;
                  break;
                case "tool-search-end":
                  this.isSearching = false;
                  break;
                case "tool-open-content" :
                  if(payload.contentId && payload.contentType) {
                    this.findAndOpenContent({
                      content_id: payload.contentId,
                      content_type: payload.contentType,
                    });
                  }
                  break;
                case "book-meeting":
                  this.pendingMeetingBooking = true;
                  break;
              }
            });
          } catch (error) {
            console.error("Error parsing:", error);
          }
        });

        timeLog("Done");

        if (signal.aborted) return;

        if (!response) throw new Error("No response");

        if (this.pendingMeetingBooking) {
          this.pendingMeetingBooking = false;

          await new Promise(resolve => setTimeout(resolve, 3000));

          this.showMeetingBooking();
        }

        if (response.navigateUrl) {
          await new Promise(resolve => setTimeout(resolve, 500));
          this.navigateToUrl(response.navigateUrl);
        }

        let recomendation = null;

        if (response.recommendations && this.resourceCollection && this.resourceCollection.length > 0) {
          if (Array.isArray(response.recommendations)) {
            const contentSet = new Set(response.recommendations.map((rec) => `${rec.content_id}-${rec.content_type}`));

            recomendation = this.resourceCollection.filter((item) => {
              return contentSet.has(`${item.content_id}-${item.content_type}`);
            });
          }
        } else if (response.recommendations && (!this.resourceCollection || this.resourceCollection.length === 0)) {
          recomendation = Array.isArray(response.recommendations) ? response.recommendations : [response.recommendations];
        }

        if (Array.isArray(recomendation)) {
          const uniqueMap = new Map();
          recomendation.forEach((item) => {
            if (item && item.content_id !== undefined && item.content_type !== undefined) {
              const key = `${item.content_id}-${item.content_type}`;
              if (!uniqueMap.has(key)) {
                uniqueMap.set(key, item);
              }
            }
          });
          recomendation = Array.from(uniqueMap.values());
        }

        if (signal.aborted) return;

        this.updateAiMessage(messageIndex, {
          message: response.message,
          recomendation: recomendation ? (Array.isArray(recomendation) ? recomendation : [recomendation]) : [],
          action: "chat",
        });

        if (this.isDesktop && this.pageElements && this.pageElements.length > 0 && this.isValidateGuideElements(this.pageElements)) {
          this.handleSidebarAlign();

          this.triggerElementGuide(this.pageElements).then(() => {
            this.pageElements = [];
            this.isChatRunning = false;
          });
        } else {
          this.isChatRunning = false;
        }

        if (signal.aborted) return;
      } catch (error) {
        if (error.message === "Aborted" || signal.aborted) return;

        this.pendingMeetingBooking = false;
        console.error("[Agent Chat Widget V2] Send message error:", error);
        this.addAiMessage("Sorry, I encountered an error. Please try again.", [], "chat");
        this.isChatRunning = false;
      } finally {
        clearTimeout(timerId);
        this.isLoading = false;
        this.isSearching = false;
        this.messageAbortController = null;
      }
    },
    handleSuggestionClick(suggestion) {
      this.handleSendMessage(suggestion);
    },
    handleChatStop() {
      if (this.messageAbortController) {
        this.messageAbortController.abort();
        this.messageAbortController = null;
      }

      if (this.triggerElementGuideCleanUp) {
        this.triggerElementGuideCleanUp();
      }

      this.stopSpeechAudio();

      // Handle stopped navigation
      if (this.isNavigationPending && this.pendingNavigation) {
        const { message } = this.pendingNavigation;
        if (message) {
          // Display message immediately if user cancelled
          const messageIndex = this.chatMessages.length - 1;
          if (messageIndex >= 0 && this.chatMessages[messageIndex].isAiMessage) {
            this.updateAiMessage(messageIndex, { message });
          }
        }
        this.isNavigationPending = false;
        this.pendingNavigation = null;
      }

      this.pendingMeetingBooking = false;

      this.isChatRunning = false;
      this.isLoading = false;
      this.isSearching = false;
    },
    showMeetingBooking() {
      if (!this.settingsMeetingEnabled || !this.meetingConfiguration) return;

      this.isMeetingOpen = true;
    },
    async queryLlmModel(data = {}, signal = null, onPartialData = null) {
      const lastMessages = this.chatMessages
        .slice(-30)
        .map((msg) => {
          let text = msg.message || "";

          if (msg.isAiMessage && Array.isArray(msg.recomendation) && msg.recomendation.length > 0) {
            const recList = msg.recomendation.map((r) => `- ${r.title || r.name || r.content_id} (content_id: ${r.content_id}, content_type: ${r.content_type})`).join("\n");
            text += `\n\nRecommended content:\n${recList}`;
          }
          
          return {
            role: msg.isAiMessage ? "assistant" : "user",
            text,
          };
        })
        .filter((msg) => msg.text.trim());

      const requestData = {
        question: data.question,
        speak: data.speak ?? false,
        history: lastMessages,
        demo_center_id: this.publicId,
        meeting: this.settingsMeetingEnabled,
        stream: true,
        date_time: new Date().toISOString(),
      };

      const response = await fetch(`${process.env.VUE_APP_API_URL}frontend/demo-center/chat/${this.publicId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      if (!onPartialData) {
        const text = await response.text();
        try {
          return JSON.parse(text);
        } catch (_) {
          return { answer: text };
        }
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunkStr = decoder.decode(value, { stream: true });

        try {
          onPartialData(chunkStr);
        } catch (e) {
          console.error("onPartialData callback error", e);
        }
      }

      try {
        return JSON.parse(chunkStr);
      } catch (_) {
        return { answer: "" };
      }
    },
    addUserMessage(text) {
      this.chatMessages.push({
        message: text,
        isAiMessage: false,
        action: "chat",
        timestamp: Date.now(),
      });
    },
    addAiMessage(text, recommendations = [], action = "chat") {
      let index = this.chatMessages.push({
        message: text,
        isAiMessage: true,
        recomendation: recommendations,
        action: action,
        timestamp: Date.now(),
      });

      return index - 1;
    },
    updateAiMessage(index, options = {}) {
      if (this.chatMessages[index]) {
        this.chatMessages[index] = {
          ...this.chatMessages[index],
          ...options,
        };

        this.chatMessages = [...this.chatMessages];
      }
    },
    processPendingNavigationMessage() {
      if (!this.pendingNavigation) return;

      const { message, audioUrl, suggestions } = this.pendingNavigation;

      if (message) {
        setTimeout(() => {
          this.isLoading = true;

          this.$nextTick(() => {
              this.$refs.chatInterface.scrollToBottom();
          });

          setTimeout(() => {
            this.isLoading = false;

            this.addAiMessage(message, [], "chat");

            // Restore suggestions if present
            if (suggestions) {
              this.chatSuggestions = suggestions;
            }

            // Play audio after message appears
            if (audioUrl) {
              setTimeout(() => this.playSpeechAudio(audioUrl), 300);
            }

            this.$nextTick(() => {
              this.$refs.chatInterface.smoothScrollToLastMessage();
            });

            // Cleanup
            this.pendingNavigation = null;
            this.isNavigationPending = false;
          }, 800);
        }, 500);
      }
    },
    showWelcomeMessage() {
      if (!this.initialChatOpen) {
        this.initialChatOpen = true;

        setTimeout(() => {
          this.isLoading = true;

          setTimeout(() => {
            this.isLoading = false;

            const welcomeMsg = generateWelcomeMessage();
            
            this.addAiMessage(welcomeMsg, [], "chat");
          }, 700);
        }, 500);
      }
    },
    showAiChatMessage(message) {
      setTimeout(() => {
        this.isLoading = true;

        setTimeout(() => {
          this.isLoading = false;
          this.addAiMessage(message, [], "chat");
        }, 700);
      }, 500);
    },
    isValidateGuideElements(elements) {
      return (
        elements
          .map((element) => {
            const domElement = document.querySelector(element.selector);
            if (!domElement) return null;
            const rect = domElement.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return null;
            return domElement;
          })
          .filter((element) => element).length > 0
      );
    },
    async triggerElementGuide(elements) {
      if (elements && elements.length > 0) {
        const elementGuides = elements
          .map((element) => {
            const domElement = document.querySelector(element.selector);
            if (!domElement) return null;
            const rect = domElement.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return null;
            return {
              message: element.message,
              element: domElement,
            };
          })
          .filter((element) => element && element.element);

        if (elementGuides.length === 0) {
          return Promise.resolve();
        }

        if (this.elementGuideAbortController) {
          this.elementGuideAbortController.abort();
        }

        this.elementGuideAbortController = new AbortController();
        const signal = this.elementGuideAbortController.signal;
        this.isElementGuideRunning = true;

        const cleanup = () => {
          if (this.elementGuideAbortController) {
            this.elementGuideAbortController.abort();
            this.elementGuideAbortController = null;
          }
          this.isElementGuideRunning = false;
          if (this.$refs.cursor) {
            this.$refs.cursor.hideMessage();
          }
        };

        this.triggerElementGuideCleanUp = cleanup;

        let index = 0;
        let startPosition = { x: 0, y: 0 };

        try {
          for (const elementGuide of elementGuides) {
            if (signal.aborted) break;
            index++;
            this.$refs.cursor.hideMessage();
            await cancellableSleep(500, signal);
            if (signal.aborted) break;
            elementGuide.element.scrollIntoView({ behavior: "smooth", block: "center" });
            await cancellableSleep(1000, signal);
            if (signal.aborted) break;

            const position = getElementTopLeftScreenCordinate(elementGuide.element);

            const { endPosition } = await this.$refs.cursor.animateCursor({
              start: startPosition,
              end: position,
              action: "hover",
              target: elementGuide.element,
              signal: signal,
            });

            if (signal.aborted) break;

            const scrollX = window.scrollX || window.pageXOffset || 0;
            const scrollY = window.scrollY || window.pageYOffset || 0;

            startPosition = {
              x: endPosition.x - scrollX,
              y: endPosition.y - scrollY,
            };

            if (signal.aborted) break;
          }
        } catch (error) {
          if (error.message !== "Aborted") {
            throw error;
          }
        } finally {
          this.triggerElementGuideCleanUp = null;
          this.elementGuideAbortController = null;
          this.isElementGuideRunning = false;
        }
      }

      return Promise.resolve();
    },
    handlePlayWelcomeGreeting() {
      const greetings = GREETINGS.marin;
      const file = greetings[Math.floor(Math.random() * greetings.length)];
      const url = `${process.env.VUE_APP_SHARE_URL}audio/${file}`;
      this.playSpeechAudio(url);
    },
    onAudioLevelUpdate(level) {
      if (this.$refs.chatInterface) {
        this.$refs.chatInterface.updateAudioLevel(level);
      }
    },
    onSpeechAudioPlay() {
      if (this.$refs.chatInterface) {
        this.$refs.chatInterface.setAudioPlaying();
      }
    },
    onSpeechAudioStop(options = {}) {
      if (this.$refs.chatInterface) {
        this.$refs.chatInterface.setAudioStopped();

        if (options.checkVoiceRecorder) {
          this.$refs.chatInterface.checkAndRestartVoiceRecorder();
        }
      }
    },
    navigateToUrl(url) {
      this.saveSnapshot({ action: "resume" });
      window.open(url, "_self");
    },
    handleOpenContent(content) {
      this.currentContent = content;
      this.isContentPanelOpen = true;
      this.isLayout = LAYOUTS.FULL;
    },
    findAndOpenContent(options = {}) {
      const foundContent = this.resourceCollection.find((item) => item.content_id === options.content_id && item.content_type === options.content_type);

      if (foundContent) {
        this.handleOpenContent(foundContent);
      }
    },
    handleCloseContent() {
      this.isContentPanelOpen = false;
      this.currentContent = null;
    },
    handleContentProgress(data) {},
    async loadPlaylist() {
      try {
        this.isPlaylistLoading = true;
        const response = await this.axios.get(`/frontend/demo-center/content/${this.publicId}`);
        const { data } = response.data;
        this.playlist = data || [];
      } catch (error) {
        console.error("[Agent Chat V2] Failed to load playlist:", error);
        this.playlist = [];
      } finally {
        this.isPlaylistLoading = false;
      }
    },
    async getDemoCenterResources(options = {}) {
      return this.axios.get(`/frontend/demo-center/resource/${this.publicId}`).then((response) => {
        const { data } = response.data;
        this.resources = data;
      });
    },
    handleSelectPlaylistContent(content) {
      this.currentContent = content;
      this.isContentPanelOpen = true;
      this.isLayout = LAYOUTS.FULL;
    },
    handlePlaylistPrev() {
      if (!this.hasPrev || !this.playlist?.length) return;
      const idx = this.playlist.findIndex(
        (item) =>
          item.content_id === this.currentContent.content_id &&
          item.content_type === this.currentContent.content_type
      );
      if (idx > 0) this.handleSelectPlaylistContent(this.playlist[idx - 1]);
    },
    handlePlaylistNext() {
      if (!this.hasNext || !this.playlist?.length) return;
      const idx = this.playlist.findIndex(
        (item) =>
          item.content_id === this.currentContent.content_id &&
          item.content_type === this.currentContent.content_type
      );
      if (idx >= 0 && idx < this.playlist.length - 1)
        this.handleSelectPlaylistContent(this.playlist[idx + 1]);
    },
    generateSessionId() {
      return "session_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
    },
    show() {
      this.isVisible = true;
    },
    hide() {
      this.isVisible = false;
    },
    expand() {
      this.handleExpand();
    },
    minimize() {
      this.handleSetLayout(LAYOUTS.INPUT);
    },
    sendMessage(text) {
      if (this.isExpanded) {
        this.handleSendMessage(text);
      } else {
        this.handleExpand();
        this.$nextTick(() => {
          this.handleSendMessage(text);
        });
      }
    },
    // Meeting Widget
    handleMeetingSuccess() {
      this.isMeetingOpen = false;

      this.addAiMessage(generateMeetingSuccessMessage(), [], "chat");

      this.$nextTick(() => {
        this.$refs.chatInterface.scrollToBottom();
      });
    },
  },
  watch: {
    isLayout(newVal, oldVal) {
      if (newVal === LAYOUTS.SIDEBAR) {
        if (oldVal === LAYOUTS.EXPANDED) {
          document.body.classList.add("sp_agent_chat_sidebar_space_expanded");
        } else {
          document.body.classList.add("sp_agent_chat_sidebar_space");
        }
      } else {
        document.body.classList.remove("sp_agent_chat_sidebar_space");
        document.body.classList.remove("sp_agent_chat_sidebar_space_expanded");
      }
    },
  },
};
</script>

<style lang="scss">
.sp_agent_chat_widget_v2 {
  --sp-chat-primary: #0b3398;
  --sp-chat-primary-rgb: 11, 51, 152;

  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  height: 0;
}

body.sp_agent_chat_sidebar_space {
  padding-right: 400px;
  transition: padding 0.2s ease;
}
body.sp_agent_chat_sidebar_space_expanded {
  padding-right: 400px;
  transition: padding 0.2s ease;
  transition-delay: 0.3s;
}
@media (max-width: 1023px) {
  body.sp_agent_chat_sidebar_space,
  body.sp_agent_chat_sidebar_space_expanded {
    padding-right: 0;
  }
}

@import "../assets/css/style.css";
</style>
